import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Settings, Trophy, Sparkles, ArrowLeft, Star, Lock } from "lucide-react";
import { toast } from "sonner";

const LEVELS = [
  { key: "shape", label: "Shapes", color: "bg-sky-100 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  { key: "letter", label: "Letters", color: "bg-orange-100 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  { key: "word", label: "Words", color: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  { key: "sentence", label: "Sentences", color: "bg-violet-100 text-violet-700 border-violet-200", dot: "bg-violet-500" },
];

export default function StudentDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [progress, setProgress] = useState([]);
  const [badges, setBadges] = useState([]);
  const [activeLevel, setActiveLevel] = useState("shape");

  useEffect(() => {
    api.getStudent(id).then(setStudent).catch(() => toast.error("Student not found"));
    api.listExercises().then(setExercises);
    api.listProgress(id).then(setProgress);
    api.listBadges(id).then(setBadges);
  }, [id]);

  const completedIds = new Set(progress.map(p => p.exercise_id));
  const totalStars = progress.reduce((s, p) => s + (p.stars || 0), 0);
  const filtered = exercises.filter(e => e.level === activeLevel);

  return (
    <div className="student-view min-h-screen bg-background">
      <header className="px-6 lg:px-10 py-5 flex items-center justify-between border-b border-border">
        <button onClick={() => navigate("/student")} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-to-picker">
          <ArrowLeft className="h-5 w-5" /> Switch player
        </button>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2 rounded-full bg-orange-100 text-orange-700 px-4 py-2 font-bold" data-testid="star-count">
            <Star className="h-5 w-5 fill-orange-500 text-orange-500" /> {totalStars}
          </div>
          <button
            onClick={() => navigate(`/student/${id}/profile`)}
            className="h-12 w-12 grid place-items-center rounded-2xl bg-white border border-border hover:border-orange-500"
            data-testid="open-profile-btn"
            aria-label="Settings"
          >
            <Settings className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-8 space-y-8">
        {student && (
          <section className="flex items-center gap-5">
            <img src={student.user.avatar} alt="" className="h-20 w-20 rounded-3xl object-cover bg-sky-100" />
            <div>
              <div className="text-muted-foreground">Hi there,</div>
              <h1 className="text-4xl sm:text-5xl font-black">{student.user.name}!</h1>
              <p className="text-muted-foreground mt-1">Let&apos;s trace something amazing today.</p>
            </div>
          </section>
        )}

        <section data-testid="level-tabs" className="flex flex-wrap gap-3">
          {LEVELS.map(l => {
            const active = l.key === activeLevel;
            return (
              <button
                key={l.key}
                data-testid={`tab-${l.key}`}
                onClick={() => setActiveLevel(l.key)}
                className={`rounded-full px-5 py-3 border font-bold transition ${active ? "bg-foreground text-background border-foreground" : `${l.color} hover:scale-[1.02]`}`}
              >
                <span className={`inline-block h-2 w-2 rounded-full ${l.dot} mr-2 align-middle`} />
                {l.label}
              </button>
            );
          })}
        </section>

        <section data-testid="exercise-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((ex, i) => {
            const done = completedIds.has(ex.id);
            const prev = i > 0 ? filtered[i - 1] : null;
            const locked = !done && prev && !completedIds.has(prev.id) && i > 0;
            const myStars = progress.find(p => p.exercise_id === ex.id)?.stars || 0;
            return (
              <button
                key={ex.id}
                data-testid={`exercise-card-${ex.id}`}
                disabled={locked}
                onClick={() => navigate(`/student/${id}/exercise/${ex.id}`)}
                className={`text-left rounded-3xl bg-white border p-6 min-h-[150px] transition group ${
                  locked ? "opacity-60 cursor-not-allowed" : "hover:border-orange-500 hover:-translate-y-0.5"
                } ${done ? "border-emerald-300 bg-emerald-50/50" : "border-border"}`}
              >
                <div className="flex items-start justify-between">
                  <div className="text-5xl sm:text-6xl font-black leading-none text-foreground/80 max-w-[60%] truncate" title={ex.guide_text}>
                    {ex.guide_text.length > 2 ? ex.guide_text[0].toUpperCase() : ex.guide_text}
                  </div>
                  <ExerciseStatusIcon locked={locked} done={done} myStars={myStars} />
                </div>
                <div className="mt-3">
                  <div className="font-black text-lg">{ex.title}</div>
                  <div className="text-sm text-muted-foreground">{ex.description}</div>
                </div>
              </button>
            );
          })}
        </section>

        <section data-testid="badges-section">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-orange-500" />
            <h2 className="text-2xl font-black">My badges</h2>
          </div>
          {badges.length === 0 ? (
            <div className="rounded-3xl bg-white border border-dashed border-border p-8 text-center text-muted-foreground">
              Complete exercises to earn your first badge!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {badges.map(b => (
                <div key={b.id} className="rounded-3xl bg-white border border-orange-200 p-5 animate-pop-in">
                  <div className="h-12 w-12 rounded-2xl bg-orange-100 grid place-items-center mb-3">
                    <Trophy className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="font-black">{b.name}</div>
                  <div className="text-sm text-muted-foreground">{b.description}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}


const STAR_SLOTS = [1, 2, 3];

function ExerciseStatusIcon({ locked, done, myStars }) {
  if (locked) return <Lock className="h-5 w-5 text-muted-foreground" />;
  if (done) {
    return (
      <div className="flex">
        {STAR_SLOTS.map(s => (
          <Star key={s} className={`h-5 w-5 ${s <= myStars ? "fill-orange-500 text-orange-500" : "text-muted"}`} />
        ))}
      </div>
    );
  }
  return <Sparkles className="h-5 w-5 text-orange-500" />;
}
